import { UATConstants } from 'src/Common/Models/UATConstants';
import { IUserCardDetails } from 'src/manageAction/interfaces/ManageAction';
import { UserContact, UserManager } from 'src/Models/UserProfile';
import { appInsights } from 'src/Services/Insights';
import { UsercardService } from 'src/Services/UsercardService';
import { GetUserAppInsightProperties } from 'src/utils/AppInsightProperties';

const atMicrosoftOnMicrosoft = '@microsoft.onmicrosoft.com';

const getMicrosoftEmailDomain = (value: string): string | null => {
  if (!value) return null;
  if (value.includes(atMicrosoftOnMicrosoft)) return atMicrosoftOnMicrosoft;
  if (value.includes(UATConstants.atMicrosoft)) return UATConstants.atMicrosoft;
  return null;
};

export async function GetUserPersona(ev: any, obj: any, multUser: boolean = false) {
  if (
    ev.target.nodeName !== UATConstants.PlaceholdersAndTitles.Label &&
    ev.target.nodeName !== 'I' &&
    ev.target.nodeName !== 'BUTTON'
  ) {
    const clickedElement = ev.target;
    const containerElement = ev.currentTarget;

    let userPersona;

    if (multUser) {
      userPersona = containerElement.querySelectorAll('.ms-TooltipHost')[0];
    } else {
      // Find the persona element that's closest to where the click occurred
      // Look for the persona in the same container as the clicked element
      const personaContainer = clickedElement.closest('.ms-PickerPersona-container, .ms-BasePicker-itemContainer, .normalPeoplePickerDiv');

      if (personaContainer) {
        // Look for persona within this specific container
        userPersona = personaContainer.querySelector('.ms-Persona');
      }

      // Fallback to the original logic if no container found
      if (!userPersona) {
        userPersona = containerElement.querySelectorAll('.ms-Persona')[0];
      }
    }

    if (userPersona) {
      obj.setState({
        isCalloutVisible: true,
        buttonId: 'callout-button',
        userContactDetails: new UserContact(),
        userManager: new UserManager(),
      });
      let uniqueName = multUser ? ev.target.innerHTML : userPersona.getAttribute('title');
      userPersona.setAttribute('id', 'callout-button');
      const emailDomain = getMicrosoftEmailDomain(uniqueName);
      if (uniqueName !== '' && uniqueName && emailDomain) {
        uniqueName = uniqueName.includes('@') ? uniqueName.split('@')[0] : '';
        await Promise.all([
          UsercardService.GetPersonaDetails(uniqueName),
          UsercardService.getProfilePhoto(uniqueName + emailDomain),
        ])
          .then((responses: any) => {
            const reader = new FileReader();
            let profileBase64;
            reader.readAsDataURL(responses[1]);
            reader.onloadend = () => {
              profileBase64 = reader.result;
              const results = responses.length > 0 ? responses[0][0] : '';
              if (results) {
                const managerAlias = results.orgHierarchy.split('/');
                const alias = managerAlias[managerAlias.length - 2];
                obj.setState(
                  {
                    userContactDetails: {
                      ...obj.state.userContactDetails,
                      email: results.email,
                      displayName: results.displayName,
                      jobTitle: results.jobTitle ? results.jobTitle : '',
                      department: results.department,
                      displayPicture: profileBase64,
                    },
                    managerAlias: alias,
                  },
                  () => {
                    Promise.all([
                      UsercardService.GetPersonaDetails(obj.state.managerAlias),
                      UsercardService.getProfilePhoto(
                        obj.state.managerAlias + UATConstants.atMicrosoft
                      ),
                    ])
                      .then((responses: any) => {
                        const reader = new FileReader();
                        let profileBase64;
                        reader.readAsDataURL(responses[1]);
                        reader.onloadend = () => {
                          profileBase64 = reader.result;
                          const results = responses.length > 0 ? responses[0][0] : '';
                          if (results) {
                            obj.setState({
                              userManager: {
                                ...obj.state.userManager,
                                email: results.email,
                                displayName: results.displayName,
                                jobTitle: results.jobTitle ? results.jobTitle : '',
                                displayPicture: profileBase64,
                              },
                            });
                          }
                        };
                      })
                      .catch((error) => {
                        obj.setState({
                          error: error.message,
                        });
                        appInsights.trackEvent(
                          {
                            name: UATConstants.Telemetry.GraphTokenRenewalFailed,
                          },
                          GetUserAppInsightProperties()
                        );
                        appInsights.trackException({
                          exception: new Error(error.message),
                        });
                      });
                  }
                );
              } else {
                obj.setState({
                  userManager: new UserManager(),
                  userContactDetails: {
                    ...obj.state.userContactDetails,
                    email: uniqueName + emailDomain,
                    displayName: uniqueName + emailDomain,
                    jobTitle: '',
                    department: '',
                  },
                });
              }
            };
          })
          .catch((error) => {
            obj.setState({
              error: error.message,
            });
            appInsights.trackEvent(
              {
                name: UATConstants.Telemetry.GraphTokenRenewalFailed,
              },
              GetUserAppInsightProperties()
            );
            appInsights.trackException({
              exception: new Error(error.message),
            });
          });
      }
    }
  }
}


export async function ShowCard(event: any, setData: Function) {
  event.stopPropagation();

  // Navigate from the clicked icon to find the associated persona element
  // The structure is: .currentUser > Stack > .iconContainer > FontIcon (clicked element)
  //                                      > .personaContainer > Persona
  const iconContainer = event.currentTarget.parentElement; // .iconContainer
  const stackContainer = iconContainer?.parentElement; // Stack
  const currentUserContainer = stackContainer?.parentElement; // .currentUser

  // Find the persona container within the same currentUserContainer
  const personaContainer = currentUserContainer?.querySelector('.personaContainer');

  // Get the persona's secondary text (email) from the specific persona element in this container
  const personaSecondaryText = personaContainer?.querySelector('div.ms-Persona-secondaryText div.ms-TooltipHost');
  let uniqueName = personaSecondaryText?.innerHTML || '';

  const emailDomain = getMicrosoftEmailDomain(uniqueName);
  if (!uniqueName || !emailDomain) {
    return;
  }

  setData((preData: IUserCardDetails) => {
    return {
      ...preData,
      buttonId: 'callout-button',
      isCalloutVisible: true,
      onDismissFocus: true,
      selectedUser: uniqueName,
    };
  });

  uniqueName = uniqueName.includes('@') ? uniqueName.split('@')[0] : '';

  // ...rest of the function remains the same
  Promise.all([
    UsercardService.GetPersonaDetails(uniqueName),
    UsercardService.getProfilePhoto(uniqueName + emailDomain),
  ])
    .then((responses: any) => {
      const reader = new FileReader();
      let profileBase64;
      reader.readAsDataURL(responses[1]);
      reader.onloadend = () => {
        profileBase64 = reader.result;
        const results = responses.length > 0 ? responses[0][0] : '';
        if (results) {
          const managerAlias = results.orgHierarchy.split('/');
          const alias = managerAlias[managerAlias.length - 2];
          setData((preData: IUserCardDetails) => {
            return {
              ...preData,
              userContactDetails: {
                ...preData.userContactDetails,
                email: results.email,
                displayName: results.displayName,
                jobTitle: results.jobTitle ? results.jobTitle : '',
                department: results.department,
                displayPicture: profileBase64,
              },
              managerAlias: alias,
            };
          });
          // Fetching manager details start
          Promise.all([
            UsercardService.GetPersonaDetails(alias),
            UsercardService.getProfilePhoto(alias + UATConstants.atMicrosoft),
          ])
            .then((managerResponses: any) => {
              const managerReader = new FileReader();
              let managerProfileBase64;
              managerReader.readAsDataURL(managerResponses[1]);
              managerReader.onloadend = () => {
                managerProfileBase64 = managerReader.result;
                const managerResults = managerResponses.length > 0 ? managerResponses[0][0] : '';
                if (managerResults) {
                  setData((preData: IUserCardDetails) => {
                    return {
                      ...preData,
                      userManager: {
                        ...preData.userManager,
                        email: managerResults.email,
                        displayName: managerResults.displayName,
                        jobTitle: managerResults.jobTitle ? managerResults.jobTitle : '',
                        displayPicture: managerProfileBase64,
                      },
                    };
                  });
                }
              };
            })
            .catch((error) => {
              setData((preData: IUserCardDetails) => {
                return {
                  ...preData,
                  error: error.message,
                };
              });
            });
        } else {
          setData((preData: IUserCardDetails) => {
            return {
              ...preData,
              userManager: new UserManager(),
              userContactDetails: {
                ...preData.userContactDetails,
                email: uniqueName + emailDomain,
                displayName: uniqueName + emailDomain,
                jobTitle: '',
                department: '',
              },
            };
          });
        }
      };
    })
    .catch((error) => {
      setData((preData: IUserCardDetails) => {
        return {
          ...preData,
          error: error.message,
        };
      });
    });
}

export async function PickerShowCard(event: any, user: string, obj: any) {
  event.target.setAttribute('id', 'callout-button');
  event.stopPropagation();
  obj.setState({
    buttonId: 'callout-button',
    isCalloutVisible: true,
    onDismissFocus: true,
    selectedUser: user,
  });
  const uniqueName = user.includes('@') ? user.split('@')[0] : '';
  Promise.all([
    UsercardService.GetPersonaDetails(uniqueName),
    UsercardService.getProfilePhoto(uniqueName + UATConstants.atMicrosoft),
  ])
    .then((responses: any) => {
      const reader = new FileReader();
      let profileBase64;
      reader.readAsDataURL(responses[1]);
      reader.onloadend = () => {
        profileBase64 = reader.result;
        const results = responses.length > 0 ? responses[0][0] : '';
        if (results) {
          const managerAlias = results.orgHierarchy.split('/');
          const alias = managerAlias[managerAlias.length - 2];
          obj.setState(
            {
              userContactDetails: {
                ...obj.state.userContactDetails,
                email: results.email,
                displayName: results.displayName,
                jobTitle: results.jobTitle ? results.jobTitle : '',
                department: results.department ? results.department : '',
                displayPicture: profileBase64,
              },
              managerAlias: alias,
            },
            () => {
              Promise.all([
                UsercardService.GetPersonaDetails(alias),
                UsercardService.getProfilePhoto(alias + UATConstants.atMicrosoft),
              ])
                .then((responses: any) => {
                  const reader = new FileReader();
                  let profileBase64;
                  reader.readAsDataURL(responses[1]);
                  reader.onloadend = () => {
                    profileBase64 = reader.result;
                    const results = responses.length > 0 ? responses[0][0] : '';
                    if (results) {
                      obj.setState({
                        userManager: {
                          ...obj.state.userManager,
                          email: results.email,
                          displayName: results.displayName,
                          jobTitle: results.jobTitle ? results.jobTitle : '',
                          displayPicture: profileBase64,
                        },
                      });
                    }
                  };
                })
                .catch((error) => {
                  obj.setState({
                    error: error.message,
                  });
                  appInsights.trackEvent(
                    {
                      name: UATConstants.Telemetry.GraphTokenRenewalFailed,
                    },
                    GetUserAppInsightProperties()
                  );
                  appInsights.trackException({
                    exception: new Error(error.message),
                  });
                });
            }
          );
        } else {
          obj.setState({
            userManager: new UserManager(),
            userContactDetails: {
              ...obj.state.userContactDetails,
              email: uniqueName + UATConstants.atMicrosoft,
              displayName: uniqueName + UATConstants.atMicrosoft,
              jobTitle: '',
              department: '',
            },
          });
        }
      };
    })
    .catch((error) => {
      obj.setState({
        error: error.message,
      });
      appInsights.trackEvent(
        {
          name: UATConstants.Telemetry.GraphTokenRenewalFailed,
        },
        GetUserAppInsightProperties()
      );
      appInsights.trackException({ exception: new Error(error.message) });
    });
}
